import { useTranslation } from "react-i18next";
import { InlineSelect, InputField } from "./fields";
import { Form, Col, Row } from "react-bootstrap";
import { useFieldArray, useWatch } from "react-hook-form";
import { GrAddCircle, GrSubtractCircle } from "react-icons/gr";
import ConditionForm from "./ConditionForm";
import ActionForm from "./ActionForm";
import { useProgramContext } from "./ProgramContext";
import { DEFAULT_ACTIONS, DEFAULT_CONDITIONS } from "./constants";

const HarvestingRuleForm = ({ fieldName, control, isReadOnly = false }) => {
  const { t } = useTranslation(["programs"]);
  const conditionOptions = [
    { value: "all", label: t(($) => $.programs.match.All) },
    { value: "any", label: t(($) => $.programs.match.Any) },
    { value: "true", label: t(($) => $.programs.match.Everything) },
  ];
  const { size, engine } = useProgramContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${fieldName}.conditions`,
  });

  const {
    fields: fieldsMatch,
    append: appendMatch,
    remove: removeMatch,
  } = useFieldArray({
    control,
    name: `${fieldName}.matches`,
  });

  const isMatchEverything =
    useWatch({
      control,
      name: `${fieldName}.match`,
    }) === "true";

  const isConditionEverything =
    useWatch({
      control,
      name: `${fieldName}.condition`,
    }) === "true";

  const {
    fields: actions,
    append: appendAction,
    remove: removeAction,
  } = useFieldArray({
    control,
    name: `${fieldName}.actions`,
  });

  const watchedActions = useWatch({
    control,
    name: `${fieldName}.actions`,
  });

  const isAllAsinActions =
    watchedActions?.length > 0 && watchedActions.every((a) => ["HARVEST_ASIN", "NEGATE_ASIN"].includes(a.function));

  return (
    <Row className="mb-3 mx-3">
      <Col xs={size === "sm" ? 12 : 8}>
        <Row className="my-3 ">
          <Col xs={12} className="fs-standard">
            <strong>{t(($) => $.programs.harvesting["Actions:"])}</strong>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 11, offset: 1 }}>
            {actions.map((item, index) => (
              <Row key={item.id}>
                <Col xs={12} className="d-flex align-items-center mb-2 justify-content-between pe-3">
                  <ActionForm
                    key={item.id}
                    fieldName={`${fieldName}.actions.${index}`}
                    control={control}
                    isReadOnly={isReadOnly}
                  />
                  {!isReadOnly && (
                    <span style={{ cursor: "pointer", marginLeft: "1rem" }} onClick={() => removeAction(index)}>
                      <GrSubtractCircle />
                    </span>
                  )}
                </Col>
              </Row>
            ))}
            {!isReadOnly && (
              <Row>
                <Col xs={12} className="d-flex justify-content-end pe-3">
                  <span style={{ cursor: "pointer" }} onClick={() => appendAction(DEFAULT_ACTIONS[engine])}>
                    <GrAddCircle />
                  </span>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        {!isAllAsinActions && (
          <>
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <div className="d-flex align-items-center fs-standard">
                    <strong>{t(($) => $.programs.harvesting["Match Criteria:"])} </strong>
                    <InlineSelect
                      options={conditionOptions}
                      fieldName={`${fieldName}.match`}
                      control={control}
                      rules={{ required: true }}
                      disabled={isReadOnly}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            {!isMatchEverything && (
              <Row>
                <Col xs={{ span: 11, offset: 1 }}>
                  {fieldsMatch.map((item, index) => (
                    <Row key={item.id}>
                      <Col xs={12} className="d-flex align-items-center mb-2 justify-content-between pe-3">
                        <ConditionForm
                          key={item.id}
                          fieldName={`${fieldName}.matches.${index}`}
                          control={control}
                          divider={index}
                          engine={"harvesting_query_match"}
                          isReadOnly={isReadOnly}
                        />
                        {!isReadOnly && (
                          <span style={{ cursor: "pointer", marginLeft: "1rem" }} onClick={() => removeMatch(index)}>
                            <GrSubtractCircle />
                          </span>
                        )}
                      </Col>
                    </Row>
                  ))}
                  {!isReadOnly && (
                    <Row>
                      <Col xs={12} className="d-flex justify-content-end pe-3">
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => appendMatch(DEFAULT_CONDITIONS["harvesting_match"])}
                        >
                          <GrAddCircle />
                        </span>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            )}
          </>
        )}
        <Row className="mb-3">
          <Col xs={12}>
            <Form.Group>
              <div className="d-flex align-items-center fs-standard">
                <strong>{t(($) => $.programs.harvesting["Performance Criteria:"])} </strong>
                <InlineSelect
                  options={conditionOptions}
                  fieldName={`${fieldName}.condition`}
                  control={control}
                  rules={{ required: true }}
                  disabled={isReadOnly}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
        {!isConditionEverything && (
          <Row>
            <Col xs={{ span: 11, offset: 1 }}>
              {!isConditionEverything &&
                fields.map((item, index) => (
                  <Row key={item.id}>
                    <Col xs={12} className="d-flex align-items-center mb-2 justify-content-between pe-3">
                      <ConditionForm
                        key={item.id}
                        fieldName={`${fieldName}.conditions.${index}`}
                        control={control}
                        divider={index}
                        isReadOnly={isReadOnly}
                      />
                      {!isReadOnly && (
                        <span style={{ cursor: "pointer", marginLeft: "1rem" }} onClick={() => remove(index)}>
                          <GrSubtractCircle />
                        </span>
                      )}
                    </Col>
                  </Row>
                ))}
              {!isReadOnly && (
                <Row>
                  <Col xs={12} className="d-flex justify-content-end pe-3">
                    <span style={{ cursor: "pointer" }} onClick={() => append(DEFAULT_CONDITIONS[engine])}>
                      <GrAddCircle />
                    </span>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        )}
      </Col>
      <Col xs={12} className="pt-3 px-0">
        <InputField
          label={t(($) => $.programs.harvesting.Comments)}
          fieldName={`${fieldName}.comment`}
          control={control}
          disabled={isReadOnly}
        />
      </Col>
    </Row>
  );
};

export default HarvestingRuleForm;
